<template>
  <div class="work-cover-container">
    <el-card title="更换作品封面">
      <!-- 当前封面 -->
      <div class="current-cover">
        <h3>当前封面</h3>
        <el-image 
          :src="getValidImageUrl(currentCoverUrl)" 
          class="cover-image"
          fit="contain"  
          @error="handleImageError('current')">
          <div slot="error" class="image-error">
            <i class="el-icon-picture-outline"></i>
            <p>封面加载失败</p>
          </div>
        </el-image>
      </div>
      
      <!-- 新封面预览（本地预览） -->
      <div v-if="localCoverPreview" class="new-cover-preview">
        <h3>新封面预览 <span class="status-badge">未上传</span></h3>
        <el-image 
          :src="localCoverPreview" 
          class="cover-image"
          fit="contain"  
          @error="handleLocalPreviewError">
          <div slot="error" class="image-error">
            <i class="el-icon-picture-outline"></i>
            <p>预览图加载失败</p>
          </div>
        </el-image>
        <div class="preview-actions">
          <el-button 
            type="text" 
            icon="el-icon-refresh-left" 
            @click="resetLocalPreview">
            重新选择
          </el-button>
        </div>
      </div>
      
      <!-- 上传区域（使用el-upload手动上传模式） -->
      <div v-else class="upload-section">
        <h3>选择新封面</h3>
        <el-upload
          class="upload-demo"
          action=""
          :auto-upload="false"
          :on-change="handleFileSelect"
          :show-file-list="false"
          accept="image/jpeg,image/png">
          <el-button size="small" type="primary">点击选择图片</el-button>
        </el-upload>
        <div class="file-tip">
          只能上传jpg/png文件，且不超过2MB
        </div>
      </div>
      
      <!-- 操作按钮 -->
      <div class="action-buttons">
        <el-button @click="handleCancel">取消</el-button>
        <el-button 
          type="primary" 
          @click="handleUploadAndSave"
          :disabled="!localCoverPreview || loading"
          :loading="loading">
          上传并保存
        </el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import { uploadImage } from '@/api/upload';
import { updateWorkCover } from '@/api/work';

export default {
  data() {
    return {
      currentCoverUrl: '', // 当前封面URL
      newCoverUrl: '',     // 新上传的封面URL（未保存）
      uploadUrl: process.env.VUE_APP_BASE_API + '/upload/image',
      workId: null,
      loading: false,       // 加载状态
      localCoverPreview: null,  // 本地预览URL
      fileToUpload: null,       // 待上传的文件对象
      defaultCover: require('@/assets/default-work.png') // 默认封面图片
    };
  },

  created() {
    // 获取作品ID和当前封面URL
    this.workId = this.$route.query.workId;
    this.currentCoverUrl = this.$route.query.currentCover || '';

     // 验证当前封面URL
    if (this.currentCoverUrl && !this.isValidUrl(this.currentCoverUrl)) {
      console.warn('无效的当前封面URL:', this.currentCoverUrl);
      this.currentCoverUrl = '';
    }
  },

  methods: {
    // 上传前检查
    beforeUpload(file) {
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/png';
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isJPG) {
        this.$message.error('只能上传JPG/PNG格式的图片');
        return false;
      }
      if (!isLt2M) {
        this.$message.error('图片大小不能超过2MB');
        return false;
      }
      
      return true;
    },
    
    // 上传成功处理 - 验证URL
    handleUploadSuccess(response) {
      if (response.data && response.data.url) {
        // 验证返回的URL
        if (this.isValidUrl(response.data.url)) {
          this.newCoverUrl = response.data.url;
          this.$message.success('图片上传成功，请点击"保存封面"完成更新');
        } else {
          this.$message.error('上传失败: 返回的URL格式无效');
          console.error('无效的图片URL:', response.data.url);
        }
      } else {
        this.$message.error('上传失败: 未返回图片URL');
      }
    },
    
    // 上传失败处理
    handleUploadError(error) {
      this.$message.error('上传失败: ' + error.message);
    },

    // 处理文件选择（使用el-upload的on-change事件）
    handleFileSelect(file) {
      console.log('文件选择事件触发', file);
      
      const rawFile = file.raw;
      
      // 验证文件类型
      if (!['image/jpeg', 'image/png'].includes(rawFile.type)) {
        this.$message.error('只能上传JPG/PNG格式的图片');
        return;
      }
      
      // 验证文件大小
      if (rawFile.size > 2 * 1024 * 1024) {
        this.$message.error('图片大小不能超过2MB');
        return;
      }
      
      // 本地预览
      const reader = new FileReader();
      reader.onload = (e) => {
        this.localCoverPreview = e.target.result;
        this.fileToUpload = rawFile;
        console.log('本地预览生成成功');
      };
      reader.readAsDataURL(rawFile);
    },

     // 重置文件输入
    resetFileInput() {
      const fileInput = document.querySelector('.file-input');
      if (fileInput) {
        fileInput.value = '';
      }
    },

     // 重置本地预览
    resetLocalPreview() {
      this.localCoverPreview = null;
      this.fileToUpload = null;
      this.resetFileInput();
    },
    
    // 处理本地预览错误
    handleLocalPreviewError() {
      this.$message.error('本地预览失败，请重新选择图片');
      this.resetLocalPreview();
    },

    // 处理图片加载错误
    handleImageError(type) {
      const imageType = type === 'current' ? '当前封面' : '新封面预览';
      this.$message.warning(`${imageType}图片加载失败，已显示默认图片`);
      
      // 记录错误URL以便调试
      const invalidUrl = type === 'current' ? this.currentCoverUrl : this.newCoverUrl;
      console.error(`${imageType}图片URL无效:`, invalidUrl);
    },
    
    // 重置新封面（重新上传）
    resetNewCover() {
      this.newCoverUrl = '';
    },
    
    // 取消操作
    handleCancel() {
      // 如果已上传未保存，询问是否放弃更改
      if (this.newCoverUrl) {
        this.$confirm('确定要放弃已上传的新封面吗?', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.$router.go(-1);
        }).catch(() => {
          // 取消取消操作
        });
      } else {
        this.$router.go(-1);
      }
    },
    
    // 验证URL有效性
    isValidUrl(url) {
      try {
        new URL(url);
        return true;
      } catch (e) {
        return false;
      }
    },

     // 获取有效的图片URL，无效时返回默认图片
    getValidImageUrl(url) {
      if (url && this.isValidUrl(url)) {
        return url;
      }
      return this.defaultCover;
    },

    // 上传并保存（先上传再保存）
    async handleUploadAndSave() {
      this.loading = true;
      try {
        // 验证文件对象
        if (!this.fileToUpload || !(this.fileToUpload instanceof File)) {
          throw new Error('无效的文件对象，请重新选择图片');
        }
        
        // 调用上传API，传递原始文件对象
        const uploadResponse = await uploadImage(this.fileToUpload);
        
        if (!uploadResponse.data || !uploadResponse.data.data.url) {
          throw new Error('上传失败: 未返回图片URL');
        }
        
        // 更新封面
        console.log("workId:", this.workId);
        await updateWorkCover(this.workId, { imageUrl: uploadResponse.data.data.url });
        
        this.$message.success('封面更新成功');
        this.$router.go(-1);
      } catch (error) {
        console.error('封面更新失败:', error);
        this.$message.error('操作失败: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    // 保存封面 - 先获取URL，再调用更新接口（用户确认后）
    async handleSave() {
      if (!this.newCoverUrl) {
        this.$message.warning('请先上传新封面');
        return;
      }
      
      this.loading = true;
      try {
        // 调用更新封面接口
        await updateWorkCover(this.workId, { imageUrl: this.newCoverUrl });
        
        this.$message.success('封面更新成功');
        this.$router.go(-1);
      } catch (error) {
        this.$message.error('更新封面失败: ' + error.message);
      } finally {
        this.loading = false;
      }
    }

  }
};
</script>

<style scoped>
.work-cover-container {
  max-width: 600px;
  margin: 20px auto;
  padding: 0 20px;
}

.current-cover, .new-cover-preview {
  margin-bottom: 30px;
  text-align: center;
}

.cover-image {
  width: 100%;
  max-width: 300px;
  height: 200px;
  margin: 0 auto;
  border: 1px dashed #e4e7ed;
  border-radius: 8px;
}

.upload-section {
  text-align: center;
  padding: 40px 20px;
  border: 1px dashed #e4e7ed;
  border-radius: 8px;
  margin-bottom: 30px;
}

.status-badge {
  background-color: #ffecb3;
  color: #f57c00;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 10px;
}

.preview-actions {
  margin-top: 10px;
  text-align: center;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

/* 文件输入样式 */
.file-input {
  display: none;
}

.file-input-label {
  display: inline-block;
  margin-bottom: 10px;
}

.file-tip {
  color: #909399;
  font-size: 12px;
  margin-top: 5px;
}
</style>